/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Record matching", description = "Choose the columns and fields Hightouch uses to match model rows to existing destination records.", lastUpdatedDate = new Date(1787097600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Record matching",
        description: "Choose the columns and fields Hightouch uses to match model rows to existing destination records.",
        lastUpdatedDate: "Wed Aug 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "When you configure record matching",
          "depth": 0,
          "id": "when-you-configure-record-matching"
        }, {
          "value": "Choose the destination field",
          "depth": 0,
          "id": "choose-the-destination-field"
        }, {
          "value": "Match on external IDs",
          "depth": 1,
          "id": "match-on-external-ids"
        }, {
          "value": "Choose the model column",
          "depth": 0,
          "id": "choose-the-model-column"
        }, {
          "value": "Provide additional identifiers",
          "depth": 0,
          "id": "provide-additional-identifiers"
        }, {
          "value": "Verify record matching worked",
          "depth": 0,
          "id": "verify-record-matching-worked"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h2: "h2",
      a: "a",
      img: "img",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "Record matching tells Hightouch how to find the destination record that corresponds to each row in your model."
        }), " You configure it whenever a sync needs to identify an existing record or person in your destination, not only create new ones."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams configuring a sync"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "A model with a column of unique values, and a connected destination."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "when-you-configure-record-matching",
        children: "When you configure record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Record matching applies whenever a sync needs to identify an existing record or person in your destination. That includes ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "update, upsert, and archive modes"
        }), ", modes that add or update people in a list, segment, or audience, and event or action syncs that attach data to an existing person."]
      }), "\n", _jsx(_components.p, {
        children: "Object syncs that only create or replace records — insert and mirror modes — don't use record matching, because there's no existing record to find."
      }), "\n", _jsxs(_components.p, {
        children: ["In the sync configuration, matching appears as a question rather than a section labeled \"Record matching.\" The default reads ", _jsx(_components.em, {
          children: "How should rows in your data model and records in the destination be matched?"
        }), " Individual destinations reword this question, but the task is the same: pick one column from your model and one field in your destination. Hightouch matches a model row to a destination record when the two values are equal."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/record-matching/matching-section.png",
          alt: "The record matching section of a Braze sync configuration"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Columns, fields, and properties:"
          }), " Hightouch generally uses \"column\" for an\nattribute in your source or model. Destinations may call the same attribute a\n\"field\" or a \"property.\""]
        })
      }), "\n", _jsx(_components.h2, {
        id: "choose-the-destination-field",
        children: "Choose the destination field"
      }), "\n", _jsx(_components.p, {
        children: "Start with the destination field. Each destination supports only specific identifiers for matching, and Hightouch shows those supported fields in the dropdown."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, when you sync users to ", _jsx(_components.a, {
          href: "/destinations/braze#record-matching",
          children: "Braze"
        }), ", Hightouch shows the identifiers Braze supports for matching — here, Braze External ID, User Alias Object, and Email address. The available options depend on the destination and sync type."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/record-matching/select-destination-field.png",
          alt: "Selecting the Braze field to match on"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When a destination recommends a field, Hightouch appends ", _jsx(_components.strong, {
          children: "(recommended)"
        }), " to its name. ", _jsx(_components.a, {
          href: "/destinations/salesforce#record-matching",
          children: "Salesforce"
        }), ", for example, marks its record ID and external ID fields as recommended because matching on them performs better."]
      }), "\n", _jsx(_components.p, {
        children: "Check the destination's documentation for the fields it supports and any destination-specific requirements."
      }), "\n", _jsx(_components.h3, {
        id: "match-on-external-ids",
        children: "Match on external IDs"
      }), "\n", _jsxs(_components.p, {
        children: ["Some destinations let you match on an ", _jsx(_components.strong, {
          children: "external ID"
        }), ": a unique identifier created outside the destination and stored on the destination record. This lets the destination keep its own primary keys while still linking records to your data."]
      }), "\n", _jsxs(_components.p, {
        children: ["Requirements vary by destination. ", _jsx(_components.a, {
          href: "/destinations/salesforce#record-matching",
          children: "Salesforce"
        }), ", for example, requires the matching field to be configured with the ", _jsx(_components.strong, {
          children: "External ID"
        }), " attribute, not just a field you treat as an identifier by convention. Check the destination's documentation before you configure the sync."]
      }), "\n", _jsx(_components.h2, {
        id: "choose-the-model-column",
        children: "Choose the model column"
      }), "\n", _jsxs(_components.p, {
        children: ["The model column contains the value Hightouch matches against. Because that value identifies which record to update, ", _jsx(_components.strong, {
          children: "choose a column with unique values"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["This is often the same column you set as the model's ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "primary key"
        }), ", since primary keys are already unique. You don't have to reuse the primary key, though — you can use another column as long as the destination supports it for matching and its values uniquely identify your model rows."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/record-matching/select-model-column.png",
          alt: "Selecting the model column to match on"
        })
      }), "\n", _jsx(_components.p, {
        children: "When more than one column could match, weigh two things:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Whether the destination supports the field for matching."
        }), "\n", _jsx(_components.li, {
          children: "Whether the column's values are unique."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If a model has both ", _jsx(_components.code, {
          children: "email"
        }), " and ", _jsx(_components.code, {
          children: "user_id"
        }), ", and the destination supports either, use the identifier that is unique and stable."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "The model column you match on should uniquely identify each row. Duplicate or\nempty matching values can cause ambiguous matches, rejected rows, duplicate\nrecords, or partial results, depending on the destination. Verify that the\ncolumn is unique and non-null before you run the sync."
        })
      }), "\n", _jsx(_components.h2, {
        id: "provide-additional-identifiers",
        children: "Provide additional identifiers"
      }), "\n", _jsx(_components.p, {
        children: "Record matching uses a single model column. To match on a combination of values, combine them into one column in your model, then match on that column."
      }), "\n", _jsxs(_components.p, {
        children: ["Separately, some destinations accept additional identifiers alongside the matching column and use them for their own identity resolution. ", _jsx(_components.a, {
          href: "/destinations/mparticle#additional-identifiers",
          children: "mParticle"
        }), ", for example, lets you supply more user or device identifiers after you choose the matching column. Check the destination's documentation for what it supports."]
      }), "\n", _jsx(_components.h2, {
        id: "verify-record-matching-worked",
        children: "Verify record matching worked"
      }), "\n", _jsx(_components.p, {
        children: "Before you run the full sync, test one row to confirm it updates the record you expect:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the sync configuration page, click ", _jsx(_components.strong, {
            children: "Test"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select a row whose destination record you can look up."
        }), "\n", _jsx(_components.li, {
          children: "Run the test."
        }), "\n", _jsx(_components.li, {
          children: "In the destination, confirm that Hightouch updated the record you expected."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A sync test can succeed at the request level even when matching is wrong, so check the destination itself. If the test creates a new record instead of updating the existing one, confirm that the model value matches the selected destination field and that the destination uses that field for matching."
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["After you set matching, map the rest of your model columns to destination fields in ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
